import { useEffect, useState } from 'react';
import { getFromStorage, storeInLocal } from 'src/lib/storage';
import { useDispatch, useSelector } from 'react-redux';
import { getDeletedEncounters } from '../../store/actions/encounter';
import { Table as AntTable, Tooltip, Select, Empty } from 'antd';
import { restoreEncounter } from '../../store/actions/encounter';
import { RestoreIcon } from 'src/icons';
import './AuraRecycleBin.scss';
import { classNames } from 'src/utilities';
import moment from 'moment';
import { listPractitioners } from 'src/store/actions/practitioner.action';
import { ProfileLogo } from '../baseComponents';
import ReactQuill from 'react-quill';
import { listSummaries } from '../../store/actions/summary';

const AuraRecycleBin = ({ isMinimized }) => {
  const dispatch = useDispatch();
  const created_at_gt_param = moment()
    .subtract(30, 'days')
    .startOf('day')
    .tz(moment.tz.guess())
    .format();

  const { deletedEncounters, deletedEncountersLoading } = useSelector(
    (state) => state?.encounterState
  );
  const { practitionerList, currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { summaryList, summaryListLoading } = useSelector(
    (state) => state?.summaryState
  );

  let hideTimeout = null;

  const [visible, setVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    per_page: 10,
  });
  const [selectedPractitioner, setSelectedPractitioner] = useState(() => {
    const storedValue = getFromStorage(
      `${currentPractitioner?.org_uuid}_practitionerFilterAuraBin`
    );
    return storedValue
      ? Number(storedValue)
      : currentPractitioner?.practitioner_id;
  });
  const [sortOrder, setSortOrder] = useState('desc');
  const [options, setOptions] = useState([]);
  const [selectedPractitionerObj, setSelectedPractitionerObj] = useState(null);
  const [currentSummary, setCurrentSummary] = useState(null);

  useEffect(() => {
    dispatch(listPractitioners());
    dispatch(
      getDeletedEncounters({
        deleted: true,
        page: pagination.current,
        per_page: pagination.per_page,
        practitioner_id: selectedPractitioner,
        sort_by: 'created_at',
        sort_direction: sortOrder,
        created_at_gt: created_at_gt_param,
      })
    );
  }, [pagination.current, pagination.per_page, sortOrder]);

  useEffect(() => {
    if (!deletedEncountersLoading && deletedEncounters?.length > 0) {
      // console.log(deletedEncounters);
    }
  }, [deletedEncounters, deletedEncountersLoading]);

  useEffect(() => {
    // console.log('options', options);
    const practitionerObj = options.find(
      (option) => option.value === selectedPractitioner
    );
    setSelectedPractitionerObj(practitionerObj);
    // console.log('selectedPractitionerObj', selectedPractitionerObj);
  }, [options, selectedPractitioner]);

  useEffect(() => {
    const newOptions =
      practitionerList?.map((practitioner, index, array) => {
        const practitionersWithSameName = array.filter(
          (p) => p.full_name === practitioner.full_name && p !== practitioner
        );
        const displayEmail = practitionersWithSameName.length > 0;
        return {
          value: practitioner.practitioner_id,
          label: (
            <div>
              <div>{practitioner.full_name}</div>
              {displayEmail && (
                <div
                  style={{
                    color: 'grey',
                    fontSize: 'smaller',
                    fontFamily: 'Poppins',
                  }}
                >
                  {practitioner.practitioner_email}
                </div>
              )}
            </div>
          ),
          name: practitioner.full_name,
        };
      }) || [];

    setOptions(newOptions);
  }, [practitionerList]);

  useEffect(() => {
    if (!summaryListLoading && summaryList?.length > 0) {
      const sortedSummaries = [...summaryList].sort(
        (a, b) => b.version - a.version
      );
      const cleanedSummary =
        typeof sortedSummaries[0].summary_json === 'string'
          ? sortedSummaries[0].summary_json.replaceAll('\n', '<br>')
          : sortedSummaries[0].summary_json;
      setCurrentSummary({
        ...sortedSummaries[0],
        summary_json: cleanedSummary,
      });
    }
  }, [summaryList, summaryListLoading]);

  const handlePageChange = (page) => {
    setPagination({
      ...pagination,
      current: page,
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter) {
      setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    }
  };

  const handleMouseEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
    }
    setVisible(true);
  };

  const handleMouseLeave = () => {
    hideTimeout = setTimeout(() => {
      setVisible(false);
    }, 100);
  };

  const statusOptionsMap = {
    completed: {
      title: 'Completed',
      value: 'completed',
      className:
        'text-[#1ec990] bg-[#eefaf4] px-4 h-8 inline-block leading-8 rounded-full whitespace-nowrap',
    },
    inprogress: {
      title: 'In Progress',
      value: 'inprogress',
      className:
        'text-[#ff6f00] bg-[#ffe1ca] px-4 h-8 inline-block leading-8 rounded-full whitespace-nowrap',
    },
    new: {
      title: 'Not Started',
      value: 'new',
      className:
        'text-[#5a8dda] bg-[#e7f1ff] px-4 h-8 inline-block leading-8 rounded-full whitespace-nowrap',
    },
  };

  const tableColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (_, record) => {
        return (
          <div className="flex items-center gap-2">
            <ProfileLogo
              name={`${record?.practitioner_first_name} ${record?.practitioner_last_name}`}
              className={
                'profile-popover-logo mr-1 sm:mr-2 lg:mr-3 profile-logo-responsive'
              }
              practitioner_name={record?.practitioner_name}
              practitioner_id={record?.practitioner_id}
            />
            {record.in_visit_status?.toLowerCase() === 'completed' ? (
              <Tooltip
                placement="right"
                title={
                  <div style={{ width: '500px' }}>
                    <ReactQuill
                      value={
                        currentSummary?.encounter_id !== record.encounter_id
                          ? 'Loading...'
                          : currentSummary?.summary_json
                      }
                      readOnly={true}
                      theme="snow"
                      modules={{ toolbar: false }}
                      className="custom-quill"
                    />
                  </div>
                }
                overlayStyle={{
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  overflow: 'auto',
                }}
              >
                <span
                  onMouseEnter={() => {
                    dispatch(
                      listSummaries(record.encounter_id, 'in-visit', 'ambient')
                    );
                  }}
                >
                  {record.description}
                </span>
              </Tooltip>
            ) : (
              <span>{record.description}</span>
            )}
          </div>
        );
      },
      width: '35%',
      className: 'description-column',
    },
    {
      title: 'Practitioner',
      dataIndex: 'practitioner_name',
      key: 'practitioner',
      width: '20%',
      className: 'practitioner-column',
    },
    {
      title: 'Status',
      dataIndex: 'in_visit_status',
      key: 'status',
      width: '15%',
      render: (_, record) => {
        const status = statusOptionsMap[record.in_visit_status?.toLowerCase()];
        return status ? (
          <div className={status.className}>{status.title}</div>
        ) : (
          <div className="text-gray-500 bg-gray-100 px-4 h-8 inline-block leading-8 rounded-full whitespace-nowrap">
            Unknown
          </div>
        );
      },
      className: 'status-column',
    },
    {
      title: 'Created at',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_, record) => {
        return (
          <div>
            {moment(record.created_at).format('MMM Do, YYYY')}{' '}
            {moment(record.created_at).format('h:mm A')}
          </div>
        );
      },
      width: '20%',
      sorter: true,
      showSorterTooltip: false,
      className: 'created-at-column',
    },
    {
      title: null,
      width: '5%',
      render: (record) => {
        const handleClick = () => {
          dispatch(restoreEncounter(record.encounter_id)).then(() => {
            dispatch(
              getDeletedEncounters({
                deleted: true,
                page: pagination.current,
                per_page: pagination.per_page,
                practitioner_id: selectedPractitioner,
                sort_by: 'created_at',
                sort_direction: sortOrder,
                created_at_gt: created_at_gt_param,
              })
            );
          });
        };

        return (
          <Tooltip title="Restore">
            <button
              onClick={handleClick}
              className="text-lg w-full h-full flex items-center justify-center mx-auto"
            >
              <RestoreIcon className="m-auto" />
            </button>
          </Tooltip>
        );
      },
      className: 'restore-column',
    },
  ];

  const onChange = (value, option, type) => {
    if (type === 'practitioner') {
      value &&
        storeInLocal(
          `${currentPractitioner?.org_uuid}_practitionerFilterAuraBin`,
          value
        );
      setSelectedPractitioner(value || null);
      if (value) {
        dispatch(
          getDeletedEncounters({
            deleted: true,
            page: pagination.current,
            per_page: pagination.per_page,
            practitioner_id: value,
            sort_by: 'created_at',
            sort_direction: sortOrder,
            created_at_gt: created_at_gt_param,
          })
        );
      }
    }
  };

  return (
    <div className={`page-wrapper`}>
      <div className="flex justify-start gap-4">
        <div
          id="PractitionerFilter"
          className="bg-white shadow-sm rounded-xl md:w-44 lg:w-48 xl:w-60 2xl:w-52 flex items-center h-10"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {selectedPractitionerObj && (
            <Select
              open={visible}
              id={`practitioner-filter`}
              name={`practitioner-filter`}
              disabled={false}
              style={{ WebkitTextFillColor: '#000' }}
              variant="borderless"
              placeholder={`Select practitioner`}
              className={classNames(
                'border-none bg-white rounded-xl py-1  outline-none w-full'
              )}
              options={options}
              popupClassName={`font-sans`}
              onChange={(e, option) => onChange(e, option, 'practitioner')}
              value={{
                label: selectedPractitionerObj.name,
                value: selectedPractitioner,
              }}
              showSearch
              filterOption={(inputValue, option) =>
                option && option.name
                  ? option?.name
                      ?.toLowerCase()
                      .indexOf(inputValue.toLowerCase()) !== -1
                  : false
              }
              mode={null}
              dropdownRender={(menu) => (
                <div id={`custom-dropdown-id`}>{menu}</div>
              )}
            />
          )}
        </div>
      </div>
      <div className="mt-5">
        <AntTable
          columns={tableColumns}
          rowKey={(record) => record.encounter_id}
          dataSource={deletedEncounters?.encounters}
          loading={deletedEncountersLoading}
          className={`encounter-bin-table`}
          onChange={handleTableChange}
          pagination={{
            total: deletedEncounters?.total,
            pageSize: pagination.per_page,
            onChange: handlePageChange,
            showSizeChanger: false,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No deleted encounters in the last 30 days"
              />
            ),
          }}
        />
      </div>
    </div>
  );
};

export default AuraRecycleBin;
